import apiClient from '../axiosConfig';
import { components } from '../types/openapi';

// Tipos del OpenAPI
type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type LegalConsulationForClientResponse = components['schemas']['LegalConsulationForClientResponse'];
type LegalConsulationForLawyerResponse = components['schemas']['LegalConsulationForLawyerResponse'];
type PaginationLegalConsulationResponse = components['schemas']['Pagination_LegalConsulationResponse_'];
type PaginationLegalConsulationForClientResponse = components['schemas']['Pagination_LegalConsulationForClientResponse_'];
type PaginationLegalConsulationForLawyerResponse = components['schemas']['Pagination_LegalConsulationForLawyerResponse_'];
type BodyCreateLegalConsulation = components['schemas']['Body_create_legal_consulation_api_core_legal_consulations__post'];
type AssignToLawyerSerializer = components['schemas']['AssignToLawyerSerializer'];
type RescheduleLegalConsulation = components['schemas']['RescheduleLegalConsulation'];
type UserMoreBasicResponse = components['schemas']['app__users__serializer__user__UserMoreBasicResponse'];

/**
 * Servicio de consultas legales
 * Maneja todas las operaciones relacionadas con consultas legales
 * Basado en los endpoints del OpenAPI
 */
class LegalConsultationService {
  /**
   * Obtener consultas legales (Admin)
   * GET /api/core/legal-consulations/admin
   */
  async getAdminConsultations(params?: {
    time_zone?: string;
    consultation_date?: string | null;
    init_consultation_time?: string | null;
    end_consultation_time?: string | null;
    description?: string;
    status?: string;
    status_payment?: string;
    client?: number;
    lawyer?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginationLegalConsulationResponse> {
    const response = await apiClient.get<PaginationLegalConsulationResponse>('/core/legal-consulations/admin', { params });
    return response.data;
  }

  /**
   * Obtener consultas legales del abogado
   * GET /api/core/legal-consulations/lawyer
   */
  async getLawyerConsultations(params?: {
    time_zone?: string;
    consultation_date?: string | null;
    init_consultation_time?: string | null;
    end_consultation_time?: string | null;
    description?: string;
    status?: string;
    status_payment?: string;
    client?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginationLegalConsulationForLawyerResponse> {
    const response = await apiClient.get<PaginationLegalConsulationForLawyerResponse>('/core/legal-consulations/lawyer', { params });
    return response.data;
  }

  /**
   * Obtener consultas legales del cliente
   * GET /api/core/legal-consulations/me
   * NOTA: El endpoint cambió de /client a /me según OpenAPI
   */
  async getClientConsultations(params?: {
    time_zone?: string;
    consultation_date?: string | null;
    init_consultation_time?: string | null;
    end_consultation_time?: string | null;
    description?: string;
    status?: string;
    status_payment?: string;
    lawyer?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginationLegalConsulationForClientResponse> {
    const response = await apiClient.get<PaginationLegalConsulationForClientResponse>('/core/legal-consulations/me', { params });
    return response.data;
  }

  /**
   * Obtener disponibilidad de un abogado en una fecha y hora específica
   * Usa el endpoint de admin para obtener consultas del abogado en un rango de fechas/horas
   */
  async getLawyerAvailability(
    lawyerId: number,
    consultationDate: string,
    initTime: string,
    endTime: string,
    excludeConsultationId?: number
  ): Promise<boolean> {
    // VALIDAR parámetros requeridos
    if (!lawyerId || !consultationDate || !initTime || !endTime) {
      console.warn('Parámetros inválidos para verificar disponibilidad:', {
        lawyerId,
        consultationDate,
        initTime,
        endTime
      });
      return false;
    }
    
    try {
      // Obtener consultas del abogado en esa fecha
      const consultations = await this.getAdminConsultations({
        lawyer: lawyerId,
        consultation_date: consultationDate,
        status: 'P', // Solo pendientes
        limit: 100, // Obtener todas las consultas del día
      });

      // Verificar si hay conflictos de horario
      const hasConflict = (consultations.results || []).some((consultation) => {
        // Excluir la consulta actual si se está editando
        if (excludeConsultationId && consultation.id === excludeConsultationId) {
          return false;
        }

        // Verificar si hay solapamiento de horarios
        if (!consultation.init_consultation_time || !consultation.end_consultation_time) {
          return false;
        }

        const consultationStart = consultation.init_consultation_time;
        const consultationEnd = consultation.end_consultation_time;

        // Verificar solapamiento: si el nuevo horario se solapa con algún horario existente
        return (
          (initTime >= consultationStart && initTime < consultationEnd) ||
          (endTime > consultationStart && endTime <= consultationEnd) ||
          (initTime <= consultationStart && endTime >= consultationEnd)
        );
      });

      return !hasConflict; // Disponible si no hay conflictos
    } catch (error) {
      console.error('Error al verificar disponibilidad:', error);
      return false; // En caso de error, asumir no disponible
    }
  }

  /**
   * Obtener una consulta legal por ID
   * GET /api/core/legal-consulations/{pk}
   */
  async getConsultationById(pk: number): Promise<LegalConsulationResponse> {
    const response = await apiClient.get<LegalConsulationResponse>(`/core/legal-consulations/${pk}`);
    return response.data;
  }

  /**
   * Crear una nueva consulta legal
   * POST /api/core/legal-consulations/
   */
  async createConsultation(data: BodyCreateLegalConsulation): Promise<LegalConsulationResponse> {
    // IMPORTANTE: Si data es FormData, NO establecer Content-Type manualmente
    // El navegador lo establecerá automáticamente con el boundary correcto
    const config: any = {};
    if (!(data instanceof FormData)) {
      // Solo establecer Content-Type si NO es FormData
      config.headers = {
        'Content-Type': 'application/json',
      };
    }
    const response = await apiClient.post<LegalConsulationResponse>('/core/legal-consulations/', data, config);
    return response.data;
  }

  /**
   * Obtener abogados disponibles para una consulta legal específica
   * GET /api/core/legal-consulations/lawyers/available/{case_id}
   * Según OpenAPI: available_lawyers_api_core_legal_consulations_lawyers_available__case_id__get
   * Devuelve solo los abogados disponibles para asignar a esa consulta
   */
  async getAvailableLawyersForCase(caseId: number): Promise<UserMoreBasicResponse[]> {
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 LegalConsultationService.getAvailableLawyersForCase - Llamando endpoint según OpenAPI:');
      console.log('   Path: /api/core/legal-consulations/lawyers/available/{case_id}');
      console.log('   Operation: available_lawyers_api_core_legal_consulations_lawyers_available__case_id__get');
      console.log('   Case ID:', caseId);
      console.log('   Expected: UserMoreBasicResponse[]');
    }
    
    try {
      const response = await apiClient.get<UserMoreBasicResponse[]>(`/core/legal-consulations/lawyers/available/${caseId}`);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ LegalConsultationService.getAvailableLawyersForCase - Respuesta recibida:', {
          status: response.status,
          statusText: response.statusText,
          dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
          dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
          data: response.data,
        });
      }
      
      if (!response.data) {
        console.warn('⚠️ LegalConsultationService.getAvailableLawyersForCase - Respuesta vacía o null');
        return [];
      }
      
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ LegalConsultationService.getAvailableLawyersForCase - Error completo:', {
          message: error.message,
          code: error.code,
          response: {
            data: error.response?.data,
            status: error.response?.status,
            statusText: error.response?.statusText,
          },
          request: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
          },
        });
      }
      throw error;
    }
  }

  /**
   * Asignar abogado a una consulta
   * POST /api/core/legal-consulations/assign-to-lawyer
   */
  async assignToLawyer(data: AssignToLawyerSerializer): Promise<LegalConsulationResponse> {
    const response = await apiClient.post<LegalConsulationResponse>('/core/legal-consulations/assign-to-lawyer', data);
    return response.data;
  }

  /**
   * Agregar documento a una consulta
   * POST /api/core/legal-consulations/add-document/{pk}
   * Según OpenAPI: add_document_legal_consulation_api_core_legal_consulations_add_document__pk__post
   * Body: multipart/form-data con file (binary) y type_file (string)
   * type_file: "I" -> Resumen de Inteligencia Artificial, "N" -> Documento normal
   */
  async addDocument(
    pk: number,
    file: File,
    typeFile: string
  ): Promise<LegalConsulationResponse> {
    // Validar que el archivo existe
    if (!file) {
      throw new Error('El archivo es requerido');
    }

    // Validar que typeFile tiene un valor válido
    if (!typeFile || (typeFile !== 'N' && typeFile !== 'I')) {
      console.warn('⚠️ typeFile no es válido, usando "N" por defecto');
      typeFile = 'N';
    }

    const formData = new FormData();
    // IMPORTANTE: Según OpenAPI, tanto 'file' como 'type_file' van en el body del FormData
    // NO como query parameter
    formData.append('file', file, file.name);
    formData.append('type_file', typeFile);

    // Debug: verificar que el archivo se está agregando correctamente
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 addDocument - Preparando petición:', {
        pk,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        typeFile,
        formDataKeys: Array.from(formData.keys()),
        formDataEntries: Array.from(formData.entries()).map(([key, value]) => ({
          key,
          value: value instanceof File ? { name: value.name, size: value.size, type: value.type } : value
        })),
      });
    }

    try {
      // IMPORTANTE: NO establecer Content-Type manualmente para FormData
      // El navegador lo establecerá automáticamente con el boundary correcto
      const response = await apiClient.post<LegalConsulationResponse>(
        `/core/legal-consulations/add-document/${pk}`,
        formData
        // type_file ahora va en el FormData, no como query parameter
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ addDocument - Respuesta exitosa:', response.data);
      }

      return response.data;
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ addDocument - Error completo:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          responseHeaders: error.response?.headers,
          requestConfig: {
            url: error.config?.url,
            method: error.config?.method,
            params: error.config?.params,
            headers: error.config?.headers,
            data: error.config?.data instanceof FormData ? 'FormData' : error.config?.data,
          },
        });
        
        // Mostrar detalles del error de validación si es 422
        if (error.response?.status === 422 && error.response?.data) {
          console.error('📋 Detalles del error de validación (422):', JSON.stringify(error.response.data, null, 2));
        }
      }
      throw error;
    }
  }

  /**
   * Eliminar documento de una consulta
   * DELETE /api/core/legal-consulations/delete-document/{pk}/{file}
   */
  async deleteDocument(pk: number, fileId: number): Promise<LegalConsulationResponse> {
    const response = await apiClient.delete<LegalConsulationResponse>(
      `/core/legal-consulations/delete-document/${pk}/${fileId}`
    );
    return response.data;
  }

  /**
   * Reagendar una consulta legal
   * PUT /api/core/legal-consulations/reschedule
   */
  async rescheduleConsultation(data: RescheduleLegalConsulation): Promise<LegalConsulationForClientResponse> {
    const response = await apiClient.put<LegalConsulationForClientResponse>(
      '/core/legal-consulations/reschedule',
      data
    );
    return response.data;
  }

  /**
   * Cancelar una consulta legal
   * PUT /api/core/legal-consulations/cancel/{pk}
   */
  async cancelConsultation(pk: number): Promise<LegalConsulationForClientResponse> {
    const response = await apiClient.put<LegalConsulationForClientResponse>(
      `/core/legal-consulations/cancel/${pk}`
    );
    return response.data;
  }
}

export default new LegalConsultationService();

